"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const faqItems: FaqItem[] = [{
  id: "item-1",
  question: "What is an inflatable mechanical bull?",
  answer: "An inflatable mechanical bull is a powered mechanical bull surrounded by an inflatable safety arena. The bull itself is mechanical, while the surrounding landing area is inflatable."
}, {
  id: "item-2",
  question: "Are inflatable mechanical bulls fully inflatable?",
  answer: "Usually not. The term generally describes a powered mechanical bull used with an inflatable safety arena. Buyers should check the exact listing because fully inflatable novelty attractions are a different type of product."
}, {
  id: "item-3",
  question: "Can I buy an inflatable mechanical bull?",
  answer: "Yes. Mechanical Bull Shop lists mechanical bull equipment for sale, including systems that may include or support an inflatable safety arena. Check the individual product package before making an enquiry."
}, {
  id: "item-4",
  question: "Does an inflatable mechanical bull come with a blower?",
  answer: "It depends on the package. Confirm whether the blower is included and whether its requirements match the exact inflatable before purchasing."
}, {
  id: "item-5",
  question: "How much space does an inflatable mechanical bull need?",
  answer: "Space requirements vary according to the bull and inflatable arena. Use the documented assembled dimensions for the exact system rather than one generic footprint."
}, {
  id: "item-6",
  question: "Can the inflatable be replaced separately?",
  answer: "In many systems the inflatable is a separate component, but any replacement must be compatible with the bull setup, dimensions, center opening, blower requirements and relevant attachment points."
}, {
  id: "item-7",
  question: "Should I buy a new or used inflatable mechanical bull?",
  answer: "That depends on your budget and the individual equipment. New equipment provides a known starting condition, while a used package can reduce the initial cost but requires closer review of both the mechanical bull and inflatable."
}, {
  id: "item-8",
  question: "How much does an inflatable mechanical bull cost?",
  answer: "Prices vary according to the machine, condition, controller, inflatable, blower, accessories and delivery requirements. Compare current complete packages rather than relying on one generic market price."
}];
export default function InflatableFaq() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="inflatablemechanicalbullfaq" data-nav="light" className="border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          {/* Header block */}
          <motion.div initial={prefersReducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="border-l-4 border-primary pl-4 md:pl-6">
            <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl uppercase">
              Inflatable Mechanical Bull FAQ
            </h2>
            <p className="mt-2 text-base text-muted-foreground sm:text-lg">
              Straight answers to common questions about inflatable mechanical bulls, safety arenas, package contents, setup, pricing and buying.
            </p>
          </motion.div>

          {/* Accordion List */}
          <div className="mt-10 space-y-3">
            <Accordion type="single" collapsible className="w-full space-y-3">
              {faqItems.map((item, index) => <div key={item.id} data-index={index}>
                  <AccordionItem value={item.id} className="border border-border bg-card px-5 py-1 text-card-foreground shadow-xs transition-colors hover:border-primary/50">
                    <AccordionTrigger className="font-serif text-left text-lg font-semibold tracking-wide text-foreground uppercase hover:no-underline hover:text-primary sm:text-xl">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="pt-2 pb-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </div>

          {/* Action Footer */}
          <motion.div initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.2
        }} className="mt-12 flex flex-col items-start justify-between gap-6 border border-border bg-muted/40 p-6 sm:flex-row sm:items-center">
            <div>
              <h3 className="font-serif text-xl font-bold tracking-wide uppercase text-foreground">
                Need information about a specific package?
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Review the individual product information or send us the machine you are considering so the available package details can be reviewed.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              <Button asChild variant="outline" className="rounded-none border-border">
                <Link to="/SetupSafetyAndMaintenancePage">
                  Read Setup &amp; Safety Guide
                </Link>
              </Button>
              <Button asChild className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/RequestQuotePage" className="inline-flex items-center gap-2">
                  Request Quote
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}